(function () {
  const MODAL_SELECTOR = '[data-thorens-region-modal]';
  const OPEN_SELECTOR = '[data-thorens-region-open]';
  const CLOSE_SELECTOR = '[data-thorens-region-close]';
  const OPTION_SELECTOR = '[data-thorens-region-option]';
  const SECTION_SELECTOR = '.thorens-region-modal__section';
  const CONFIG_ID = 'ThorensLocalizationConfig';
  const GEO_KEY = 'thorens_geo_applied_v3';

  /** @type {{ currentCountry: string, currentLocale: string, availableCountries: string[], targetLocales: string[] } | null} */
  let config = null;

  function readConfig() {
    if (config) return config;
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      config = JSON.parse(node.textContent || '{}');
    } catch {
      config = null;
    }
    return config;
  }

  function getModal() {
    return document.querySelector(MODAL_SELECTOR);
  }

  function openModal() {
    const modal = getModal();
    if (!modal) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const sections = [...modal.querySelectorAll(SECTION_SELECTOR)];
    const activeSection = sections.find((section) => section.querySelector(`${OPTION_SELECTOR}.is-active`));
    sections.forEach((section, index) => {
      section.open = !isMobile || section === activeSection || (!activeSection && index === 0);
    });

    modal.hidden = false;
    document.body.classList.add('thorens-region-modal-open');
    modal.querySelector(CLOSE_SELECTOR)?.focus();
  }

  function closeModal() {
    const modal = getModal();
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('thorens-region-modal-open');
  }

  function submitSelection(countryCode, localeCode, markGeoDone = true) {
    const form = document.getElementById('ThorensLocalizationForm');
    if (!form) return;

    const countryInput = form.querySelector('[name="country_code"]');
    const languageInput = form.querySelector('[name="language_code"]');
    const returnToInput = form.querySelector('[name="return_to"]');

    if (countryInput && countryCode) countryInput.value = countryCode;
    if (languageInput && localeCode) languageInput.value = localeCode;
    if (returnToInput) returnToInput.value = window.location.pathname + window.location.search;

    if (markGeoDone) {
      try {
        sessionStorage.setItem(GEO_KEY, '1');
      } catch {
        /* ignore */
      }
    }

    form.submit();
  }

  /** Curated market countries → locale (matches region modal). Aliases for labeled neighbors. */
  const MARKET_COUNTRY_LOCALE = {
    US: 'en',
    CA: 'en',
    GB: 'en',
    FR: 'fr',
    LU: 'fr',
    DE: 'de',
    AT: 'de',
    IT: 'it',
    ES: 'es',
    NL: 'nl',
    PL: 'pl',
    PT: 'pt-PT',
    SE: 'sv',
    CZ: 'cs',
    DK: 'da',
    IS: 'is',
    TR: 'tr',
    AU: 'en',
    JP: 'ja',
    KR: 'ko',
    VN: 'vi',
    SG: 'en',
    MX: 'es',
  };

  /** Locale → preferred country when geo country is unknown / remapped */
  const LOCALE_COUNTRY = {
    en: 'US',
    fr: 'FR',
    de: 'DE',
    es: 'ES',
    it: 'IT',
    nl: 'NL',
    pl: 'PL',
    ja: 'JP',
    ko: 'KR',
    sv: 'SE',
    cs: 'CZ',
    vi: 'VN',
    da: 'DK',
    is: 'IS',
    'pt-PT': 'PT',
    tr: 'TR',
  };

  const DEFAULT_COUNTRY = 'US';
  const DEFAULT_LOCALE = 'en';

  function normalizeLocale(code) {
    if (!code) return '';
    if (code === 'pt') return 'pt-PT';
    return code;
  }

  function pickAvailableCountry(preferred, availableCountries) {
    if (preferred && availableCountries.includes(preferred)) return preferred;
    if (availableCountries.includes(DEFAULT_COUNTRY)) return DEFAULT_COUNTRY;
    return availableCountries[0] || DEFAULT_COUNTRY;
  }

  /**
   * Geo → country + locale.
   * In curated list → local language + market currency country.
   * Otherwise → English / US (or first available country).
   */
  function resolveTarget(detectedCountry, detectedLanguage, availableCountries, targetLocales) {
    const countries = availableCountries || [];
    const localeSet = new Set(targetLocales || []);
    localeSet.add(DEFAULT_LOCALE);

    const country = (detectedCountry || '').toUpperCase();
    const mappedLocale = MARKET_COUNTRY_LOCALE[country];

    if (mappedLocale && localeSet.has(mappedLocale)) {
      // Prefer the geo country when Markets includes it; AT/LU map to DE/FR markets.
      const marketCountry =
        country === 'AT' ? 'DE' : country === 'LU' ? 'FR' : country;
      return {
        country: pickAvailableCountry(marketCountry, countries),
        locale: mappedLocale,
      };
    }

    // Browser / Shopify suggested a supported language but country is outside our list
    const lang = normalizeLocale(detectedLanguage);
    if (lang && lang !== DEFAULT_LOCALE && localeSet.has(lang) && LOCALE_COUNTRY[lang]) {
      return {
        country: pickAvailableCountry(LOCALE_COUNTRY[lang], countries),
        locale: lang,
      };
    }

    // Not in curated list → default English
    return {
      country: pickAvailableCountry(DEFAULT_COUNTRY, countries),
      locale: DEFAULT_LOCALE,
    };
  }

  async function autoLocalizeFromGeo() {
    const cfg = readConfig();
    if (!cfg) return;
    if (window.Shopify?.designMode) return;

    try {
      if (sessionStorage.getItem(GEO_KEY)) return;
    } catch {
      /* ignore */
    }

    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      'country[enabled]': 'true',
      'language[enabled]': 'true',
    });

    try {
      const response = await fetch(`${root}browsing_context_suggestions.json?${params.toString()}`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return;

      const data = await response.json();
      const top = data.suggestions?.[0]?.parts;
      const detectedCountry =
        top?.country?.handle || data.detected_values?.country?.handle || window.Shopify?.country;
      const detectedLanguage =
        top?.language?.handle || data.detected_values?.language?.handle || window.Shopify?.locale;

      const target = resolveTarget(
        detectedCountry,
        detectedLanguage,
        cfg.availableCountries || [],
        cfg.targetLocales || [],
      );

      const sameCountry = target.country === cfg.currentCountry;
      const sameLocale = target.locale === cfg.currentLocale;
      if (sameCountry && sameLocale) {
        try {
          sessionStorage.setItem(GEO_KEY, '1');
        } catch {
          /* ignore */
        }
        return;
      }

      submitSelection(target.country, target.locale, true);
    } catch {
      /* network / blocked — stay on current locale */
    }
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest(OPEN_SELECTOR)) {
      event.preventDefault();
      openModal();
      return;
    }

    if (event.target.closest(CLOSE_SELECTOR)) {
      event.preventDefault();
      closeModal();
      return;
    }

    const option = event.target.closest(OPTION_SELECTOR);
    if (option && !option.disabled && !option.classList.contains('is-disabled')) {
      event.preventDefault();
      submitSelection(option.dataset.country, option.dataset.locale, true);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = getModal();
    if (modal && !modal.hidden) closeModal();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoLocalizeFromGeo);
  } else {
    autoLocalizeFromGeo();
  }
})();
